import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Edit } from "lucide-react";
import type { SystemSetting } from "@shared/schema";

const settingFormSchema = z.object({
  key: z.string(),
  value: z.string().transform((val, ctx) => {
    try {
      return JSON.parse(val);
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid JSON format",
      });
      return z.NEVER;
    }
  }),
  description: z.string().optional(),
});

type SettingFormValues = z.infer<typeof settingFormSchema>;

export default function SystemSettings() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<SystemSetting | null>(null);

  const { data: settings = [], isLoading } = useQuery<SystemSetting[]>({
    queryKey: ["/api/admin/settings"],
  });

  const form = useForm<SettingFormValues>({
    resolver: zodResolver(settingFormSchema),
    defaultValues: {
      key: "",
      value: "",
      description: "",
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ key, value, description }: { key: string; value: any; description?: string }) => {
      const res = await apiRequest("PUT", `/api/admin/settings/${key}`, {
        value,
        description,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
      form.reset();
      setEditingSetting(null);
      setDialogOpen(false);
      toast({
        title: "Setting updated",
        description: "System setting has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating setting",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleOpenDialog = (setting: SystemSetting) => {
    setEditingSetting(setting);
    form.reset({
      key: setting.key,
      value: JSON.stringify(setting.value, null, 2),
      description: setting.description || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = (values: SettingFormValues) => {
    updateMutation.mutate({
      key: values.key,
      value: values.value,
      description: values.description,
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold" data-testid="text-page-title">
          System Settings
        </h1>
        <p className="text-muted-foreground mt-2">
          Configure system-wide settings
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : settings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No settings configured yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {settings.map((setting) => (
                  <TableRow key={setting.id} data-testid={`setting-row-${setting.key}`}>
                    <TableCell className="font-medium">{setting.key}</TableCell>
                    <TableCell className="max-w-md truncate font-mono text-sm">
                      {JSON.stringify(setting.value)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {setting.description || "—"}
                    </TableCell>
                    <TableCell>
                      <Button aria-label="Edit setting"
                        variant="outline"
                        size="icon"
                        onClick={() => handleOpenDialog(setting)}
                        data-testid={`button-edit-setting-${setting.key}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="dialog-setting-form">
          <DialogHeader>
            <DialogTitle>Edit Setting</DialogTitle>
            <DialogDescription>
              Update the system setting value and description.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Key</FormLabel>
                    <FormControl>
                      <Input {...field} disabled data-testid="input-setting-key" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Value (JSON)</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        className="font-mono text-sm"
                        rows={6}
                        data-testid="input-setting-value"
                      />
                    </FormControl>
                    <FormDescription>
                      Must be valid JSON format
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Optional description..."
                        rows={2}
                        data-testid="input-setting-description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateMutation.isPending}
                  data-testid="button-submit-setting"
                >
                  Update
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
